import axios from "axios";
import { getPreSignedUrl } from "../redux/actions/apiActions";

const sanitizeFilename = (name) => {
  const base = (name || '').toString().split('/').pop(); // no path fragments
  const clean = base.replace(/[^a-zA-Z0-9._-]/g, '_');
  return clean.length ? clean.replace(/^\.*/, '') : `file_${Date.now()}`;
};

/**
 * Presign + upload to S3 (single or multiple files).
 * 
 * @param {File|File[]} files - one File or an array of Files
 * @param {Object} meta - { entity, entityId }
 * @param {Function} onProgress - optional (index, file, percent)
 * @returns {Promise<Array<{fileName, fileType, key, url}>>}
 */
export async function s3Upload(files, meta, onProgress) {
  const fileArr = Array.isArray(files) ? files : [files];

  if (fileArr.length === 0) return [];

  // 1) ask backend for presigned URLs
  const { data } = await getPreSignedUrl({
    files: fileArr.map((f) => ({ fileName: f.name, fileType: f.type })),
    entity: meta.entity,
    entityId: meta.entityId,
  });


  const presigned = data?.uploads || [];
  const byName = new Map(presigned.map((u) => [u.fileName, u]));

  // console.log("byName", byName);

  // 2) upload all in parallel
  await Promise.all(
    fileArr.map((file, idx) => {
      const u = byName.get(sanitizeFilename(file.name));
      if (!u?.uploadUrl) throw new Error(`Missing presigned URL for ${file.name}`);

      return axios.put(u.uploadUrl, file, {
        headers: { "Content-Type": file.type },
        withCredentials: false, // don't send cookies to S3
        transformRequest: (v) => v, // keep file as-is
        onUploadProgress: (e) => {
          if (e.total && onProgress) {
            const pct = Math.round((e.loaded / e.total) * 100);
            onProgress(idx, file, pct);
          }
        },
      });
    })
  );

  // 3) normalize result
  return fileArr.map((file) => {
    const u = byName.get(sanitizeFilename(file.name));
    return {
      fileName: u.fileName,
      fileType: u.fileType,
      key: u.key,
      url: u.fileUrl,
    };
  });
}